import {LabelCheckbox} from '../../Components/Atoms/Forms'
import {P} from '../../Components/Atoms/Typography'
import {Baseline} from '../../Components/Layout/Grid'
import {AdjustableContainer, NarrowRightSidebar} from '../../Components/Layout/Layout'
import {OrgState} from '../../Components/Views/Users/EditUserAccountSettingsDetails'
import {AnyOrgLevel, availableKeyValues, OffsetCheckboxList} from '../../Components/Views/Utils'
import React, {useState} from 'react'

interface ManagerLevelCheckboxProps {
  isChainWide: boolean
  setIsChainWide: (isChainWide: boolean) => void
  onClick: (data: string, isChainToggled: boolean) => void
  handleLocationClick: (siteIds: string[], locId: string) => void
  handleGroupClick: (locationIds: string[], siteIds: string[], groupId: string) => void
  data: AnyOrgLevel
  selectedLocations: string[]
  selectedSites: OrgState[]
  disableNextLevel?: boolean
  writableIds: string[]
  isAllowedToEditSite: boolean
}

const isChecked = (selectedSites: string[], isChainWide: boolean, selectedLocations: string[], dataId: string) => {
  if (isChainWide || selectedSites.includes(dataId) || selectedLocations.includes(dataId)) return true
  return false
}

const ManagerLevelCheckbox = ({
  data,
  isChainWide,
  setIsChainWide,
  onClick,
  handleLocationClick,
  handleGroupClick,
  selectedLocations,
  selectedSites,
  disableNextLevel,
  writableIds,
  isAllowedToEditSite
}: ManagerLevelCheckboxProps) => {
  const keys = Object.keys(data) as Array<keyof AnyOrgLevel>
  const arr = keys.find(k => availableKeyValues.includes(k)) as 'locationGroups' | 'locations' | 'brands' | 'sites'
  // Default checked behavior for nodes (may be overridden for group-level below)
  let checked = isChecked(
    selectedSites.map(s => s.id),
    isChainWide,
    selectedLocations,
    data.id
  )

  // If this node is a location group (has .locations), compute group-level checked state
  const isLocationGroup = Boolean((data as any).locations && Array.isArray((data as any).locations))
  let groupLocationIds: string[] = []
  let groupSiteIds: string[] = []
  if (isLocationGroup) {
    const locations = (data as any).locations as AnyOrgLevel[]
    groupLocationIds = locations.map(l => l.id)
    groupSiteIds = locations.reduce((acc: any[], l) => acc.concat((l.sites || []).map((s: any) => s.id)), [])
    const allGroupLocationsSelected =
      groupLocationIds.length > 0 && groupLocationIds.every(id => selectedLocations.includes(id))
    // For display, mark group checked if chain-wide or all locations in the group are selected
    checked = isChainWide || allGroupLocationsSelected
  }

  const hasWritableDescendant = (node: AnyOrgLevel): boolean => {
    if (writableIds.includes(node.id)) return true
    return availableKeyValues.some(
      key =>
        Array.isArray((node as any)[key]) && (node as any)[key].some((item: AnyOrgLevel) => hasWritableDescendant(item))
    )
  }

  if (!hasWritableDescendant(data)) {
    return null
  }

  // Show checkbox for writable nodes, nodes that have sites (locations), or location groups
  const showCheckbox = writableIds.includes(data.id) || data.sites || isLocationGroup
  if (data.visible !== false) {
    return (
      <>
        {showCheckbox ? (
          <LabelCheckbox
            disabled={isAllowedToEditSite ? disableNextLevel : true}
            checked={checked}
            readOnly
            flipped={false}
            label={data.name}
            name={arr}
            onClick={() => {
              // Brand-level management toggle
              if (data.brands) {
                return onClick(data.id, true)
              }

              // Location group-level toggle: toggle all locations in the group
              if (isLocationGroup) {
                return handleGroupClick(groupLocationIds, groupSiteIds, data.id)
              }

              // Location-level toggle (has sites)
              if (data.sites) {
                const siteIds = data.sites.map(site => site.id)
                return handleLocationClick(siteIds, data.id)
              }

              // Fallback single-id toggle
              return onClick(data.id, false)
            }}
          />
        ) : (
          <P>{data.name}</P>
        )}
        {!!arr && (
          <OffsetCheckboxList>
            {data[arr]!.map(item => (
              <li key={item.id}>
                <ManagerLevelCheckbox
                  writableIds={writableIds}
                  selectedLocations={selectedLocations}
                  selectedSites={selectedSites}
                  isChainWide={isChainWide}
                  setIsChainWide={setIsChainWide}
                  data={item}
                  onClick={onClick}
                  handleLocationClick={handleLocationClick}
                  handleGroupClick={handleGroupClick}
                  disableNextLevel={checked}
                  isAllowedToEditSite={isAllowedToEditSite}
                />
              </li>
            ))}
          </OffsetCheckboxList>
        )}
      </>
    )
  } else {
    const items = data[arr]!
    return (
      <>
        {items.map(item => (
          <ManagerLevelCheckbox
            key={item.id}
            writableIds={writableIds}
            selectedLocations={selectedLocations}
            selectedSites={selectedSites}
            isChainWide={isChainWide}
            setIsChainWide={setIsChainWide}
            data={item}
            onClick={onClick}
            handleLocationClick={handleLocationClick}
            handleGroupClick={handleGroupClick}
            isAllowedToEditSite={isAllowedToEditSite}
          />
        ))}
      </>
    )
  }
}

interface SiteSelectionProps {
  infoText: string
  writableIds: string[]
  chain: AnyOrgLevel
  isChainWide: boolean
  setIsChainWide: any
  isLoading: boolean
  selectedSites: string[]
  setSelectedSites: any
  t: any
}

const SiteSelectionCheckboxStyle = ({
  infoText,
  writableIds,
  chain,
  isLoading,
  selectedSites,
  isChainWide,
  setSelectedSites,
  setIsChainWide,
  t
}: SiteSelectionProps) => {
  const [selectedLocations, setSelectedLocations] = useState([] as string[])

  const getWritableSiteIds = (node: AnyOrgLevel): string[] => {
    if (node.sites) {
      return node.sites.map(site => site.id).filter(siteId => writableIds.includes(siteId))
    }

    return availableKeyValues.reduce<string[]>((siteIds, key) => {
      const children = (node as any)[key] as AnyOrgLevel[] | undefined
      return children ? siteIds.concat(children.flatMap(child => getWritableSiteIds(child))) : siteIds
    }, [])
  }

  // Handle group-level selection: toggles all locations in a group
  const handleGroupClick = (locationIds: string[], siteIds: string[], groupId: string) => {
    const allSelected = locationIds.every(id => selectedLocations.includes(id))
    const updatedLocations = allSelected
      ? selectedLocations.filter(locationId => !locationIds.includes(locationId))
      : Array.from(new Set([...selectedLocations, ...locationIds]))
    setSelectedLocations(updatedLocations)

    if (!allSelected) {
      const updatedSelectedSites = Array.from(new Set([...selectedSites, ...siteIds]))
      setSelectedSites(updatedSelectedSites)
    } else {
      const updatedSelectedSites = selectedSites.filter(siteId => !siteIds.includes(siteId))
      setSelectedSites(updatedSelectedSites)
    }
  }

  const handleManagementLevelClick = (id: string, isChainToggled: boolean) => {
    if (isChainToggled && isChainWide) {
      const updatedSelectedSites: string[] = []
      const updatedLocations: string[] = []
      setSelectedSites(updatedSelectedSites)
      setSelectedLocations(updatedLocations)
    } else if (isChainToggled && !isChainWide) {
      const updatedSelectedSites: string[] = getWritableSiteIds(chain)
      const updatedLocations: string[] = []
      setSelectedSites(updatedSelectedSites)
      setSelectedLocations(updatedLocations)
    } else {
      const updatedSelectedSites = selectedSites.includes(id)
        ? selectedSites.filter(siteId => siteId !== id)
        : [...selectedSites, id]
      setSelectedSites(updatedSelectedSites)
    }
    if (isChainToggled) {
      setIsChainWide(!isChainWide)
    }
  }

  const handleLocationClick = (siteIds: string[], locId: string) => {
    const updatedLocations = selectedLocations.includes(locId)
      ? selectedLocations.filter(locationId => locationId !== locId)
      : [...selectedLocations, locId]
    setSelectedLocations(updatedLocations)

    if (updatedLocations.includes(locId)) {
      const updatedSelectedSites = Array.from(new Set([...selectedSites, ...siteIds]))
      setSelectedSites(updatedSelectedSites)
    } else {
      const updatedSelectedSites = selectedSites.filter(siteId => !siteIds.includes(siteId))
      setSelectedSites(updatedSelectedSites)
    }
  }

  return (
    <>
      {!isLoading && (
        <NarrowRightSidebar>
          <AdjustableContainer padding="2rem">
            <Baseline>
              <P variant="small">{infoText}</P>
            </Baseline>
            <ManagerLevelCheckbox
              writableIds={writableIds}
              data={chain}
              isChainWide={isChainWide}
              setIsChainWide={setIsChainWide}
              onClick={handleManagementLevelClick}
              handleLocationClick={handleLocationClick}
              handleGroupClick={handleGroupClick}
              selectedLocations={selectedLocations}
              selectedSites={selectedSites.map(id => ({level: 'sites', id}))}
              isAllowedToEditSite={true}
            />
          </AdjustableContainer>
        </NarrowRightSidebar>
      )}
    </>
  )
}

export default SiteSelectionCheckboxStyle
